import React from 'react';

const OrderTimeline = ({ order, isExpanded, onToggle }) => {
  // Define order status progression
  const statusSteps = [
    { 
      key: 'pending_payment', 
      label: 'รอชำระเงิน', 
      icon: '💳',
      color: 'yellow'
    },
    { 
      key: 'paid', 
      label: 'ชำระเงินแล้ว', 
      icon: '✅',
      color: 'blue'
    },
    { 
      key: 'shipping', 
      label: 'กำลังจัดส่ง', 
      icon: '🚚',
      color: 'purple'
    },
    { 
      key: 'delivered', 
      label: 'ส่งแล้ว', 
      icon: '📦',
      color: 'green'
    },
    { 
      key: 'received', 
      label: 'รับของแล้ว', 
      icon: '🎉',
      color: 'green'
    }
  ];

  // Find current status index
  const currentStatusIndex = statusSteps.findIndex(step => step.key === order.status);
  const currentStep = currentStatusIndex >= 0 ? currentStatusIndex : 0;

  // Get status dates
  const getStatusDate = (statusKey) => {
    switch (statusKey) {
      case 'pending_payment':
        return order.createdAt ? new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString('th-TH') : null;
      case 'paid':
        return order.paidAt ? new Date(order.paidAt?.toDate?.() || order.paidAt).toLocaleDateString('th-TH') : null;
      case 'shipping':
        return order.shippingDate ? new Date(order.shippingDate?.toDate?.() || order.shippingDate).toLocaleDateString('th-TH') : null;
      case 'delivered':
        return order.deliveredDate ? new Date(order.deliveredDate?.toDate?.() || order.deliveredDate).toLocaleDateString('th-TH') : null;
      case 'received':
        return order.receivedDate ? new Date(order.receivedDate?.toDate?.() || order.receivedDate).toLocaleDateString('th-TH') : null;
      default:
        return null;
    }
  };

  if (!isExpanded) {
    // Collapsed view - show only current status
    return (
      <div 
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="mt-3 pt-3 border-t border-gray-200 cursor-pointer"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">{statusSteps[currentStep]?.icon || '📋'}</span>
            <span className="text-sm text-gray-600">สถานะ: {statusSteps[currentStep]?.label || order.status}</span>
          </div>
          <button className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <span>ดู Timeline</span>
            <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Expanded view - show full timeline
  return (
    <div 
      onClick={(e) => e.stopPropagation()}
      className="mt-3 pt-3 border-t border-gray-200"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">Timeline</h4>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <span>ซ่อน</span>
          <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {statusSteps.map((step, index) => {
          const isCompleted = index <= currentStep;
          const isCurrent = index === currentStep;
          const statusDate = getStatusDate(step.key);

          return (
            <div key={step.key} className="flex items-start gap-3">
              {/* Timeline Line */}
              <div className="flex flex-col items-center">
                {/* Icon Circle */}
                <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm ${
                  isCompleted 
                    ? step.color === 'yellow' ? 'bg-yellow-500 text-white' :
                      step.color === 'blue' ? 'bg-blue-500 text-white' :
                      step.color === 'purple' ? 'bg-purple-500 text-white' :
                      'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-400'
                }`}>
                  {step.icon}
                </div>
                {/* Vertical Line */}
                {index < statusSteps.length - 1 && (
                  <div className={`w-0.5 h-6 md:h-8 mt-1 ${
                    isCompleted 
                      ? step.color === 'yellow' ? 'bg-yellow-300' :
                        step.color === 'blue' ? 'bg-blue-300' :
                        step.color === 'purple' ? 'bg-purple-300' :
                        'bg-green-300'
                      : 'bg-gray-200'
                  }`}></div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${
                      isCompleted ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </p>
                    {statusDate && (
                      <p className="text-xs text-gray-500 mt-0.5">{statusDate}</p>
                    )}
                  </div>
                  {isCurrent && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      step.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                      step.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                      step.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      ปัจจุบัน
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderTimeline;

